import React from 'react';
import { Link } from 'react-router-dom';
import Body from '../Components/layout/Body';
import LearnMoreModal from '../Components/layout/LearnMoreModal';
import { USERS, USERS_STEPS, TABS } from './ConstPaths';

const HOME_WELCOME_HELP = `
<div>
  <h5>Who is Access Navigator designed for?</h5>
  <p>The app is designed for practitioners to use in collaboration with their clients. Practitioners may include: assistive technology (AT) specialist, SLP, OT, or SpEd teacher (with some AT background). The app is best-suited for practitioners who have experience with AT in general and alternative access in particular.</p>
  <p>Clients include people with some form of physical disability that makes it difficult to use the standard interface for computers, tablets, and similar devices.</p>
</div>
<div>
  <h5>I'm a bit rusty or new to access assessments - how will Access Navigator help me?</h5>
  <ul>
    <li>Steps you through the process</li>
    <li>Helps you prepare for the assessment</li>
    <li>Helps you evaluate client abilities (including physical, perceptual, and cognitive)</li>
    <li>Provides ideas on access options to choose from</li>
    <li>Allows you and your client to try out and compare options for informed decisions</li>
    <li>Provides remedies that may enhance access</li>
    <li>Helps you structure recommendations</li>
    <li>Content developed by experienced therapists</li>
  </ul>
</div>
<div>
  <h5>I'm experienced at doing access assessments and do them regularly - how can Access Navigator help me?</h5>
  <ul>
    <li>Steps you through the process if you like that, but doesn't lock you in. You can pick any section of the tool that may be of benefit.</li>
    <li>Provides a place to organize and summarize your access option trials and plan for upcoming assessments</li>
    <li>Allows you to collect objective data
    <li>Allows you to sort potential prospect solutions and keep track of decisions on whether or not to move forward</li>
    <li>Provides ideas from experienced therapists on access options, tips/tricks, ideas for enhancing access</li>
    <li>Helps you structure recommendations</li>
    <li>Provides reports that can be copied / pasted or used in full form</li>
  </ul>
</div>
<div>
  <h5>Can Access Navigator help me make data-driven decisions? Yes!</h5>
  <ul>
    <li>Test-drives provide metrics on some tasks</li>
    <li>Option for defining your own tasks</li>
    <li>Allows you and the client to rate the access option being trialed</li>
    <li>Compares ratings and performance for different access options, to aid decision-making</li>
  </ul>
</div>
<div>
  <h5>Can Access Navigator help me make access decisions for clients with emerging abilities? Yes!</h5>
  <ul>
    <li>Provides a method to identify and set goals specific for these clients</li>
    <li>Allows for unstructured tasks during trials</li>
    <li>Ability to collect observational data</li>
    <li>Provides troubleshooting / remedy support</li>
  </ul>
</div>
<div>
  <h5>Can Access Navigator help me make access decisions for clients with sophisticated access needs? Yes!</h5>
  <ul>
    <li>Provides a method to identify and set goals specific for these clients</li>
    <li>Provides structured tasks / metrics during test drives, or unstructured tasks if you prefer</li>
    <li>Provides troubleshooting / remedy support</li>
  </ul>
</div>
<div>
  <h5>Can Access Navigator help me with re-evaluations? Yes!</h5>
  <ul>
    <li>Allows you to store or add-in prior history information</li>
    <li>Provides guidance for new or different access options</li>
    <li>Provides troubleshooting / remedy support</li>
  </ul>
</div>
<div>
  <h5>Does Access Navigator include the client in the evaluation process? Yes!</h5>
  <ul>
    <li>You and the client run through Access Navigator together</li>
    <li>Client's input is included throughout, including goals, wants, needs, as well as ratings and feedback after each trial</li>
  </ul>
</div>
<div>
  <h5>What can I expect when using Access Navigator?</h5>
  <p>Access Navigator provides a structure that helps you and your team make good decisions. It is not a magic wand that "does it for you." It can't (and shouldn't) take the place of your skills, but it provides support throughout, so you can be more efficient and effective.</p>
  <p>We've tried to design the app to:</p>
  <ul>
    <li>Make access assessments more fun for you and the client</li> 
    <li>Take the high-stakes worry out of access assessments</li>
    <li>Walk you through the steps in a logical sequence</li>
    <li>But let you skip around if needed or desired</li>
  </ul>
</div>
<div>
  <h5>Who developed Access Navigator?</h5>
  <p>Access Navigator was developed by an interdisciplinary team at the <a href='https://rerc-aac.psu.edu/' target='_blank'>RERC on AAC</a>. The team includes Heidi Koester, Erik Jakobs, Susan Fager, Jessica Gormley, and Tabatha Sorenson. We are grateful for the many users who provided input and feedback along the way.</p>
  <p>Please contact us with any questions or suggestions, at accessnavigator@aac-learning-center.org.</p>
  <p>This research was supported by a grant from the National Institute on Disability, Independent Living, and Rehabilitation Research (NIDILRR grant #90REGE0014) to the Rehabilitation Engineering Research Center on Augmentative and Alternative Communication (RERC on AAC). NIDILRR is a Center within the Administration for Community Living (ACL), Department of Health and Human Services (HHS). The contents of this site do not necessarily represent the policy of NIDILRR, ACL, HHS, and you should not assume endorsement by the Federal Government.</p>
</div>
`;



const DEMO_VIDEO_HTML = `
  <p>Here's a short video to get you going with Access Navigator <span class="aa-more-info">(2 min 30 sec)</span>:</p>
  <div style="padding:70% 0 0 0;position:relative;"><iframe src="https://player.vimeo.com/video/922491206?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" frameborder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write" style="position:absolute;top:0;left:0;width:100%;height:100%;" title="short-AccessNav-demo"></iframe></div><script src="https://player.vimeo.com/api/player.js"></script>
`;

const STUDY_INFO_HTML = `
  <p>In this study, you will use Access Navigator to perform a mock access evaluation with a client who needs alternative access due to a motor impairment. We'll ask you to try 2 "Test-drives" with your client:</p>
  <ol>
    <li>If the client already has an access method that they use, do the first Test-drive with that method, as a baseline for how things are going currently.</li>
    <li>The other Test-drive(s) can be whatever you and the client choose -- ideally you are trying to explore things that may benefit the client, just as you would in a "real" eval.</li>
  </ol>
  <p>For example, you might try:</p>
  <ul>
    <li>Adjustments to one access method, such as changing the settings or putting it in a different position</li>
    <li>Using a whole different access method, like a head mouse instead of a trackball, or speech instead of a keyboard for typing</li>
  </ul>
  The main point is to give you enough experience using the app so you can give us meaningful feedback. We are not judging what you choose to Test-drive or the resulting outcomes.</p>
  <hr />
  <p>With that in mind, before the session you'll need to: </p>
  <ul>
    <li>Get familiar with the Access Navigator app</li>
    <li>Choose an appropriate client to work with <span class="aa-more-info">(We are happy to discuss ideas for this.)</span></li>
    <li>Get a rough idea for what you might explore with the client during the session</li>
    <li>Consider the access methods and other equipment you'll need, and have it available. <span class="aa-more-info">(We will also work with you to remind the client to bring their current access method and related equipment with them.)</span></li>
  </ul>

`;

//content sub-component with unique content for this page
function HomeWelcomeContent(props) {
  const {
    userType
  } = props;
  return(
    <>
    {/* <p>We're glad you're here!</p> */}
    <p>Access Navigator helps you and your clients with physical disabilities make decisions about alternative access, using a <strong>structured access assessment process</strong>.</p>
    <p>Walk through the process step-by-step,  or skip to priority sections as needed.</p>
    {(userType === 'eval') ?
      <>
      <div className="aa-callout">
        <h3><i className="fa fa-hand-o-right fa-lg" aria-hidden="true"></i> If you are participating in our study:</h3>
        <p>Please complete these 3 steps to get oriented to Access Navigator and what we will be asking you to do.</p>
        <ol>
          <li>
            <LearnMoreModal 
              color="link"
              linkText="Review important info about the study"
              title="Important info about the study"
              content={STUDY_INFO_HTML}
            />
          </li>
          <li>
            <LearnMoreModal 
              color="link"
              linkText="View the demo video"
              title="View the demo video"
              content={DEMO_VIDEO_HTML}
            />
            <span className="aa-more-info"> (2min 30sec)</span>
          </li>
          <li>
              <span>Visit and explore every page in Access Navigator</span>
              <span className="aa-more-info"> (~ 15 minutes. Click <strong>Continue</strong> below to get started.)</span>
          </li>
        </ol>
        <p>Thanks very much!</p>
      </div>
      <p>Click <strong>Continue</strong> to get started without an account.</p>
      </>
      :
      <div>
        <ul className='aa-home-list'>
          <li>Gather information</li>
          <li>Evaluate abilities</li>
          <li>Test-drive solutions</li>
          <li>Make informed decisions</li>
        </ul>     
        <ul className='aa-home-list'>
          <li>Free to use, forever!</li>
          <li>Optional user accounts let you save data and reports from client sessions</li>
        </ul>
        <ul style={{listStyleType: "none"}}>
          <li><Link className="btn btn-primary" to={USERS + "/" + USERS_STEPS.CREATE}>Sign up for an account</Link></li>
        </ul>
        <p>Or, click <strong>Continue</strong> to get started without an account.</p>
      </div>   
    }
    {/* <p>Our goal is to provide guidance without locking you and your team into specific solutions, allowing for maximum flexibility during decision-making.</p> */}
    {/* <p>Additional features include: ways to collaboratively try out and rate potential access solutions, methods for collecting data on performance, remedies for common access challenges, and customizable reports.</p> */}
    <p className="aa-more-info">NOTE: This app is still under development. Some features may be incomplete or glitchy.</p>
    </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function HomeWelcome(props) {
  const {
    userType
  } = props;

  const pageData = {
    className: "home-page",
    image: "./assets/images/target-icon.jpg", //not yet used, but could supply an image on page
    faIcon: "fa-smile-o", //include this font-awesome icon in the image column of the page
    majorHeading: "Welcome to Access Navigator!",
    title: "We're glad you're here!",
    subTitle: "",
    learnMore: {linkText: "Learn More about Access Navigator",
                title: "Common questions about Access Navigator",
                content: HOME_WELCOME_HELP
                },
    nextRoute: TABS.GATHER, //studyParticipant ? EVAL_INTRO : USERS, //static name for next page since this never changes
    sidebar: false
  }

  console.log("rendering HomeWelcome");

  return(
    <>
    <Body page={pageData} contentComponent={<HomeWelcomeContent userType={userType}/>} />
    </>
  );
}


export default HomeWelcome;